/**
 * Blast — Inside-candle (HA) / Engulfing-candle (Regular) detection on the
 * Daily & Weekly timeframes, independent of the chart's display candle type /
 * interval. The detected candle's RAW High/Low forms a band that projects
 * forward 2 periods; while a band is visible, a fresh close-cross of the
 * C-POC band fires the BUY/SHORT signal (see computeBlastSignals in
 * signalIndicators.ts).
 *
 * Definitions (per the user's spec):
 *   Inside    (on HA)      : ha.high <= prevHa.high AND ha.low >= prevHa.low
 *   Engulfing (on Regular) : high > prevHigh AND low < prevLow AND
 *                            ( close > max(prevOpen, prevClose)
 *                              OR close < min(prevOpen, prevClose) )
 *
 * The band uses the detected candle's RAW high/low (the inside test runs on HA
 * but the band must reference actual price on the price pane). HA here is the
 * app's smoothed Heikin-Ashi (EMA-3), matching the chart's "HA" candle type.
 *
 * Feed CLOSED period candles only — the forming bar must be excluded so
 * detections are repaint-safe.
 */
import { CandleRow } from '../components/charts/TradingChart';
import { transformCandles } from './candleTransforms';

export type BlastKind = 'inside' | 'engulfing';

export interface BlastEvent {
  /** Bar time (IST seconds) of the detected candle. */
  time: number;
  /** Raw candle High/Low — the band edges. */
  high: number;
  low: number;
  kind: BlastKind;
}

export interface BlastWindow {
  start: number; // inclusive (detected candle's time)
  end: number;   // exclusive (detected time + (1 + projForward) periods)
  high: number;
  low: number;
}

/** Detect inside (HA) / engulfing (Regular) candles over the closed period
 *  candles. Returns one event per qualifying candle (engulfing wins the `kind`
 *  label if a bar somehow satisfies both). */
export function detectBlastEvents(closedCandles: CandleRow[]): BlastEvent[] {
  if (!closedCandles || closedCandles.length < 2) return [];
  const ha = transformCandles(closedCandles, 'HA');
  const haByTime = new Map<number, CandleRow>();
  for (const c of ha) haByTime.set(c.time, c);

  const out: BlastEvent[] = [];
  for (let i = 1; i < closedCandles.length; i++) {
    const cur = closedCandles[i];
    const prev = closedCandles[i - 1];

    // Engulfing — Regular (raw) candles.
    const engulfing =
      cur.high > prev.high &&
      cur.low < prev.low &&
      (cur.close > Math.max(prev.open, prev.close) ||
        cur.close < Math.min(prev.open, prev.close));

    // Inside — smoothed HA candles.
    const haCur = haByTime.get(cur.time);
    const haPrev = haByTime.get(prev.time);
    const inside =
      !!haCur && !!haPrev &&
      haCur.high <= haPrev.high &&
      haCur.low >= haPrev.low;

    if (engulfing || inside) {
      out.push({ time: cur.time, high: cur.high, low: cur.low, kind: engulfing ? 'engulfing' : 'inside' });
    }
  }
  return out;
}

/** Build the active band windows from events. The band is active for the
 *  `projForward` periods AFTER the detected candle (default 2) — i.e. "the next
 *  2 days/weeks" per spec — NOT the detection period itself. Starting at
 *  `e.time + periodSec` (the period after the detected candle closes) is what
 *  makes the indicator non-repainting / no look-ahead: on a lower timeframe the
 *  band never appears over the detection candle's own (still-forming or
 *  not-yet-confirmed) intraday bars. `periodSec` = 86400 (D) or 604800 (W). */
export function blastActiveWindows(
  events: BlastEvent[],
  periodSec: number,
  projForward = 2,
): BlastWindow[] {
  return events.map(e => ({
    start: e.time + periodSec,
    end: e.time + (projForward + 1) * periodSec,
    high: e.high,
    low: e.low,
  }));
}

/** IST shift applied to chart candle times (UNIX seconds). */
const IST_SHIFT = 19800;

/** Keep only period candles that have fully CLOSED as of now, so detection
 *  never runs on the still-forming Daily/Weekly bar (repaint-safe). Times are
 *  IST-shifted UNIX seconds, matching the chart axis. */
export function closedPeriodCandles(candles: CandleRow[], periodSec: number): CandleRow[] {
  const nowShifted = Math.floor(Date.now() / 1000) + IST_SHIFT;
  return candles.filter(c => (c.time as number) + periodSec <= nowShifted);
}

/** True when `time` falls inside any active band window. */
export function isInBlastWindow(time: number, windows: BlastWindow[]): boolean {
  for (const w of windows) {
    if (time >= w.start && time < w.end) return true;
  }
  return false;
}
